import React from "react";
import AppHeading from "../layout/parts/app-heading";
import AppBody from "../layout/parts/app-body";
import Card from "../components/card";
import SimpleInfoBlock from "../components/graphs/simple-info-block";
import LineChart from "../components/graphs/line-chart";
import axios, {responseHandler, errorHandler} from "../requests/axios.js";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import WeekendRoundedIcon from "@mui/icons-material/WeekendRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LocalPizzaRoundedIcon from "@mui/icons-material/LocalPizzaRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import {subDays} from "date-fns";
import {toast} from "react-toastify";
import {useAuth} from "../providers/auth-provider.jsx";
import ChannelSelector from "../components/channel-selector.jsx";
import DateSelector from "../components/date-selector.jsx";
import ListChart from "../components/graphs/list-chart.jsx";
import Loader from "../common/loader";
import dateToYmd from "../functions/date-to-ymd.js";
import GstvAgGrid from "../components/gstv-ag-grid";
import toTitleCase from "../functions/to-title-case";

export default function PerformancePage() {
    const [chartData, setChartData] = React.useState({});
    const [dateRange, setDateRange] = React.useState({
        start: subDays(new Date(), 30),
        end: new Date(),
    });
    const [channels, setChannels] = React.useState([]);
    const [selectedChannels, setSelectedChannels] = React.useState([]);
    const [channelsLoading, setChannelsLoading] = React.useState(false);

    const [programReport, setProgramReport] = React.useState([]);
    const [programReportLoading, setProgramReportLoading] = React.useState(true);

    const {user} = useAuth();

    const updateSelectedChannels = React.useCallback((channelOptions) => {
        setSelectedChannels(channelOptions);
    }, []);

    // {start, end}
    const updateDateRange = React.useCallback((rangeObject) => {
        setDateRange(rangeObject);
    }, []);

    React.useEffect(() => {
        setChannelsLoading(true);
        axios
            .get(`api/organisations/${user.organisation.id}/channels`)
            .then(
                responseHandler((response) => {
                    setChannels(response.data);
                })
            )
            .catch(errorHandler((e) => {
            }))
            .finally(() => setChannelsLoading(false));
    }, [user.organisation.id]);

    React.useEffect(() => {
        const controller = new AbortController();

        if (channels.length) {
            const payload = {
                start: dateToYmd(dateRange.start),
                end: dateToYmd(dateRange.end),
            };

            if (selectedChannels.length) {
                payload.channels = selectedChannels
                    .map((channel) => `&channels[]=${channel.value}`)
                    .join("");
            }

            axios
                .get(
                    `api/pages/performance?start_date=${payload.start}&end_date=${
                        payload.end
                    }${payload.channels ?? ""}`, { signal: controller.signal }
                )
                .then(
                    responseHandler((response) => {
                        setChartData(response.data);
                    })
                )
                .catch(
                    errorHandler((e) => {
                        if (e.name && e.name === "CanceledError") {
                            // do nothing, cancelling old request
                        } else {
                            console.error(e);
                            toast.error("Error loading performance report data");
                        }
                    })
                );
        }
        return () => {
            if(channels.length) {
                controller.abort();
            }
        };
    }, [channels.length, dateRange.end, dateRange.start, selectedChannels]);

    // table data
    React.useEffect(() => {
        const controller = new AbortController();

        if (channels.length) {
            setProgramReportLoading(true);
            const payload = {
                start: dateToYmd(dateRange.start),
                end: dateToYmd(dateRange.end),
            };


            if (selectedChannels.length) {
                payload.channels = selectedChannels
                    .map((channel) => `&channels[]=${channel.value}`)
                    .join("");
            }

            axios
                .get(
                    `api/pages/programs?start_date=${payload.start}&end_date=${
                        payload.end
                    }${payload.channels ?? ""}`, { signal: controller.signal }
                )
                .then(
                    responseHandler((response) => {
                        setProgramReport(response.data);
                        setColDefs(
                            Object.keys(response.data[0]).map((entry) => ({
                                field: entry,
                                headerName: toTitleCase(entry),
                                width: 180,
                            })),
                        );
                    })
                )
                .catch(
                    errorHandler((e) => {
                        if (e.name && e.name === "CanceledError") {
                            // do nothing, cancelling old request
                        } else {
                            console.error(e);
                            toast.error("Error loading program data");
                        }
                    })
                )
                .finally(() => setProgramReportLoading(false));
        }
        return () => {
            if(channels.length) {
                controller.abort();
            }
        };
    }, [channels.length, dateRange.end, dateRange.start, selectedChannels]);

    // ag grid
    const defaultColDef = React.useMemo(() => {
        return {
            filter: true,
            editable: false,
        };
    }, []);
    const [colDefs, setColDefs] = React.useState();

    return (
        <React.Fragment>
            <AppHeading/>

            <AppBody loading={channelsLoading}>
                <div className="performance-page">
                    <div className="performance-page__header-row">
                        <ChannelSelector
                            channels={channels}
                            onChange={updateSelectedChannels}
                        />
                        <DateSelector updateRange={updateDateRange}/>
                    </div>
                    <div className="performance-page__header-row">
                        <SimpleInfoBlock
                            label="Unique Viewers"
                            data={chartData.unique_viewers}
                            icon={<PersonRoundedIcon/>}
                        />
                        <SimpleInfoBlock
                            label="Engaged Viewers"
                            data={chartData.engaged_viewers}
                            icon={<VisibilityRoundedIcon/>}
                        />
                        <SimpleInfoBlock
                            label="Total Sessions"
                            data={chartData.total_sessions}
                            icon={<WeekendRoundedIcon/>}
                        />
                        <SimpleInfoBlock
                            label="Engaged Sessions"
                            data={chartData.engaged_sessions}
                            icon={<LocalPizzaRoundedIcon/>}
                        />
                    </div>
                    <div className="performance-page__body">
                        <div className="performance-page__left">
                            <Card height={400}>
                                <LineChart
                                    title="Minutes Watched"
                                    subtitle=""
                                    datasets={chartData.channels_minutes?.datasets || []}
                                />
                            </Card>
                        </div>
                        <div className="performance-page__right">
                            <SimpleInfoBlock
                                label="Total Minutes Watched"
                                data={chartData.total_minutes}
                                icon={<TimerRoundedIcon/>}
                            />
                            <Card>
                                <ListChart
                                    title="Programs"
                                    subtitles={["Top 10 Programs"]}
                                    datasets={
                                        chartData?.top_programs ? [chartData.top_programs] : []
                                    }
                                    icons={[<WorkspacePremiumRoundedIcon/>]}
                                />
                            </Card>
                        </div>
                    </div>
                    <div className="performance-page__lower">
                        {
                            programReportLoading ? <div>
                                <Loader/>
                            </div> : (
                                <GstvAgGrid
                                    data={programReport}
                                    defs={colDefs}
                                    defaultDef={defaultColDef}
                                />
                            )
                        }
                    </div>
                </div>
            </AppBody>
        </React.Fragment>
    );
}
